import echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { getToken, handle401Response } from '../../Utils/tokenManager';
import logger from '../../Utils/logger';

window.Pusher = Pusher;

let Echo;

try {
    // Only initialize Pusher if we have the required environment variables
    if (process.env.REACT_APP_REVERB_APP_KEY && process.env.REACT_APP_REVERB_HOST) {
        // Derive backend origin (no /api prefix) from REACT_APP_BASE_URL
        const apiBase = process.env.REACT_APP_BASE_URL || '';
        let backendOrigin = '';
        try {
            backendOrigin = new URL(apiBase).origin; // e.g. https://dev.natureland.hipster-virtual.com
        } catch {
            backendOrigin = apiBase.replace(/\/api\/.*/, '');
        }

        Echo = new echo({
            broadcaster: 'pusher',
            key: process.env.REACT_APP_REVERB_APP_KEY,
            wsHost: process.env.REACT_APP_REVERB_HOST,
            wsPort: process.env.REACT_APP_REVERB_PORT || 6001,
            wssPort: process.env.REACT_APP_REVERB_PORT || 6001,
            forceTLS: false,
            enabledTransports: ['ws', 'wss'],
            disableStats: true,
            cluster: '',
            encrypted: false,
            useTLS: false,
            path: process.env.REACT_APP_REVERB_PATH || '/app/' + process.env.REACT_APP_REVERB_APP_KEY,
            // Use authorizer to dynamically read token from localStorage each time
            // This ensures we always use the latest token, even after login
            // When using authorizer, we don't need authEndpoint
            authorizer: (channel, options) => {
                return {
                    authorize: async (socketId, callback) => {
                        // Helper function to attempt auth with given token
                        const attemptAuth = async (authToken) => {
                            const headers = {
                                'Accept': 'application/json',
                                'Content-Type': 'application/x-www-form-urlencoded',
                            };

                            if (authToken) {
                                headers['Authorization'] = `Bearer ${authToken}`;
                            }

                            const response = await fetch(`${backendOrigin}/api/broadcasting/auth`, {
                                method: 'POST',
                                headers: headers,
                                body: `socket_id=${socketId}&channel_name=${channel.name}`,
                                credentials: 'same-origin'
                            });

                            return response;
                        };

                        try {
                            // Try auth with current token
                            let token = getToken();
                            let response = await attemptAuth(token);
                            logger.log('[Echo] Broadcasting auth response status:', response.status);
                            // If 401, attempt token refresh and retry
                            if (response.status === 401) {
                                logger.log('[Echo] Broadcasting auth returned 401, attempting token refresh...');
                                const { shouldRetry, newToken } = await handle401Response();

                                if (shouldRetry && newToken) {
                                    logger.log('[Echo] Token refreshed, retrying broadcasting auth...');
                                    response = await attemptAuth(newToken);
                                } else {
                                    logger.warn('[Echo] Token refresh failed, auth cannot proceed');
                                    throw new Error('Auth failed: 401 Unauthorized (refresh failed)');
                                }
                            }

                            // Check if final response is ok
                            if (!response.ok) {
                                throw new Error(`Auth failed: ${response.status}`);
                            }

                            const data = await response.json();
                            callback(null, data);
                        } catch (error) {
                            logger.error('[Echo] Broadcasting auth error:', error);
                            callback(error, null);
                        }
                    }
                };
            }
        });

        // Reconnection and heartbeat logic
        if (Echo && Echo.connector && Echo.connector.pusher) {
            const pusher = Echo.connector.pusher;
            let reconnectAttempts = 0;
            let reconnectTimeout = null;
            let heartbeatInterval = null;
            const INITIAL_RECONNECT_DELAY = 2000; // 2 seconds
            const MAX_RECONNECT_DELAY = 30000; // 30 seconds max
            const HEARTBEAT_INTERVAL = 25000; // 25 seconds

            // Heartbeat function to prevent idle timeout
            const startHeartbeat = () => {
                if (heartbeatInterval) {
                    clearInterval(heartbeatInterval);
                }
                heartbeatInterval = setInterval(() => {
                    if (pusher.connection.state === 'connected') {
                        // Trigger connection activity to prevent idle timeout
                        // Access Pusher's internal socket to send ping
                        try {
                            const socket = pusher.connection.socket;
                            if (socket && socket.readyState === WebSocket.OPEN) {
                                // Send pusher ping message to keep connection alive
                                socket.send(JSON.stringify({ event: 'pusher:ping', data: {} }));
                            }
                        } catch (error) {
                            // If socket access fails, connection will handle pings automatically
                            logger.debug('Heartbeat ping skipped:', error.message);
                        }
                    }
                }, HEARTBEAT_INTERVAL);
            };

            // Stop heartbeat
            const stopHeartbeat = () => {
                if (heartbeatInterval) {
                    clearInterval(heartbeatInterval);
                    heartbeatInterval = null;
                }
            };

            // Reconnection function with exponential backoff
            const attemptReconnect = () => {
                if (pusher.connection.state === 'connected' || pusher.connection.state === 'connecting') {
                    return;
                }

                // Calculate delay with exponential backoff (capped at MAX_RECONNECT_DELAY)
                const delay = Math.min(INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY);
                
                logger.log(`⚠️ Socket disconnected — reconnecting in ${delay}ms...`);
                
                reconnectTimeout = setTimeout(() => {
                    reconnectAttempts++;
                    try {
                        pusher.connect();
                    } catch (error) {
                        logger.warn('Reconnection attempt failed:', error);
                        attemptReconnect();
                    }
                }, delay);
            };

            // Listen for connection state changes
            pusher.connection.bind('state_change', (states) => {
                const { previous, current } = states;
                
                if (current === 'connected') {
                    // Reset reconnect attempts on successful connection
                    if (previous !== 'connected') {
                        reconnectAttempts = 0;
                        if (reconnectTimeout) {
                            clearTimeout(reconnectTimeout);
                            reconnectTimeout = null;
                        }
                        logger.log('✅ WebSocket reconnected');
                        
                        // Dispatch custom reconnection event
                        window.dispatchEvent(new CustomEvent('socket:reconnected', {
                            detail: { timestamp: Date.now() }
                        }));
                    }
                    // Start heartbeat when connected
                    startHeartbeat();
                } else if (current === 'disconnected' || current === 'failed') {
                    stopHeartbeat();
                    attemptReconnect();
                } else if (current === 'connecting') {
                    stopHeartbeat();
                }
            });

            // Start heartbeat if already connected
            if (pusher.connection.state === 'connected') {
                startHeartbeat();
            }

            // Handle page visibility changes (when tab becomes active again)
            document.addEventListener('visibilitychange', () => {
                if (!document.hidden && pusher.connection.state !== 'connected') {
                    logger.log('⚠️ Tab became visible — checking connection...');
                    attemptReconnect();
                }
            });

            // Cleanup on page unload
            window.addEventListener('beforeunload', () => {
                stopHeartbeat();
                if (reconnectTimeout) {
                    clearTimeout(reconnectTimeout);
                }
            });
        }
    } else {
        // Create a mock Echo object if environment variables are missing
        Echo = {
            channel: () => ({
                listen: () => ({ stop: () => {} }),
                stopListening: () => {},
                subscribed: () => {},
                error: () => {},
                unsubscribe: () => {}
            }),
            private: () => ({
                listen: () => ({ stop: () => {} }),
                stopListening: () => {},
                subscribed: () => {},
                error: () => {},
                unsubscribe: () => {}
            }),
            leave: () => {}
        };
        logger.log('Pusher environment variables not found. Using mock Echo object.');
    }
} catch (error) {
    logger.warn('Failed to initialize Pusher:', error.message);
    // Create a mock Echo object as fallback
    Echo = {
        channel: () => ({
            listen: () => ({ stop: () => {} }),
            stopListening: () => {},
            subscribed: () => {},
            error: () => {},
            unsubscribe: () => {}
        }),
        private: () => ({
            listen: () => ({ stop: () => {} }),
            stopListening: () => {},
            subscribed: () => {},
            error: () => {},
            unsubscribe: () => {}
        }),
        leave: () => {}
    };
}

export default Echo;




